import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import translationKeys from '@translationKeys'

import CustomColorTooltip from '@components/CustomColorTooltip'
import LockIcon from '@components/icons/LockIcon'
import { EVENT_ACTIVITY_KEYS } from '@modules/activity/activityKeys'
import { useTrackUserActivity } from '@modules/activity/useTrackUserActivity'
import { BorderlessFilterAccordion, FilterBadge, FilterClearIcon } from '@modules/search/index.styles'
import { UserEventsOptions } from '@modules/search/types'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import SearchIcon from '@mui/icons-material/Search'
import { AccordionDetails, Box, Checkbox, InputAdornment, Typography } from '@mui/material'
import { useDebouncedValue } from '@shared/hooks/useDebouncedValue'
import useSelection from '@shared/hooks/useSelection'
import { colors } from '@theme/variables'

import { FilterSearchInput, FilterSectionAccordionSummary, FilterTitleBox, OuterFilterBox } from './index.styles'

interface SearchByEventProps {
  eventsOptions: UserEventsOptions[]
  onSubmit: (data: { includeIds: number[]; excludeIds: number[] }) => void
  eventsFilterData?: { includeIds?: number[]; excludeIds?: number[] } | null
  isFilterExpanded: boolean
  onExpand: (nextState: boolean) => void
  prefix: string
}

const EventItemRow = ({
  event,
  isSelected,
  isExcluded,
  onSelect,
}: {
  event: UserEventsOptions
  isSelected: boolean
  isExcluded: boolean
  onSelect: (id: number) => void
}) => {
  const { t } = useTranslation()
  const itemStyle = isExcluded ? { color: '#B2B2B2' } : {}

  return (
    <Box key={event.value} display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
      <Checkbox
        checked={isSelected && !isExcluded}
        onChange={() => onSelect(event.value)}
        disabled={isExcluded}
        style={{ ...itemStyle, padding: '0 9px' }}
      />
      <Box flexGrow={1} display="flex" gap="5px" alignItems="center">
        <Typography variant="body1" style={itemStyle}>
          {event.label}
        </Typography>
        {event.hidden ? (
          <CustomColorTooltip
            placement="top"
            arrow
            title={t(translationKeys.SEARCH_FILTER_BY_EVENT_TOOLTIP)}
            bgColor={colors.primary}
            maxWidth="none"
          >
            <Box display="flex" marginTop="-2px">
              <LockIcon color="primary" style={{ width: '17px' }} />
            </Box>
          </CustomColorTooltip>
        ) : null}
      </Box>
    </Box>
  )
}

export const SearchByEvent = ({
  eventsOptions,
  onSubmit,
  eventsFilterData,
  isFilterExpanded,
  onExpand,
  prefix,
}: SearchByEventProps) => {
  const { t } = useTranslation()
  const [searchTerm, setSearchTerm] = useState('')
  const { trackActivity } = useTrackUserActivity()
  const {
    selectedIds: includeEventIds,
    toggleSelectItem: toggleIncludeEvent,
    toggleSelectItems,
  } = useSelection<number>(
    eventsOptions.map(option => option.value),
    { initiallySelectedItems: eventsFilterData?.includeIds, isReactiveInitiallySelectedItems: true },
  )

  const debouncedIncludedIds = useDebouncedValue(includeEventIds, 150)

  const filteredEvents = useMemo(() => {
    if (searchTerm?.trim().length < 2) return eventsOptions
    return eventsOptions.filter(event => event.label.toLocaleLowerCase().includes(searchTerm.trim().toLocaleLowerCase()))
  }, [searchTerm, eventsOptions])

  useEffect(() => {
    if (includeEventIds.join('') !== (eventsFilterData?.includeIds || []).join('')) {
      onSubmit({ includeIds: includeEventIds, excludeIds: [] })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedIncludedIds])

  const isFilteredBy = !!(eventsFilterData?.excludeIds?.length || eventsFilterData?.includeIds?.length)

  return (
    <OuterFilterBox $active={isFilteredBy}>
      <BorderlessFilterAccordion disableGutters onChange={() => onExpand(!isFilterExpanded)} expanded={!!isFilterExpanded}>
        <FilterSectionAccordionSummary
          sx={{ flexDirection: 'row' }}
          expandIcon={<ExpandMoreIcon sx={{ transfrom: 'rotage(90deg)' }} />}
          data-testid="filter_accordion_summary"
          id="search-filter-event_section"
        >
          <FilterTitleBox>
            <Typography variant="h2">
              {t(translationKeys.SEARCH_BY_EVENT)}
              {!!isFilteredBy && (
                <FilterBadge variant="dot" color="primary" data-testid={`${prefix?.toLowerCase()}_filter_badge`} />
              )}
            </Typography>
            {!!isFilteredBy && (
              <FilterClearIcon
                id="event_filter-reset"
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation()
                  toggleSelectItems(includeEventIds)
                }}
                color="primary"
                data-testid="filter_clear_section"
              />
            )}
          </FilterTitleBox>
        </FilterSectionAccordionSummary>
        <AccordionDetails sx={{ padding: 0 }}>
          <Box display="flex" flexDirection="column" justifyContent="space-between" alignItems="center">
            <FilterSearchInput
              data-testid="event-filter-search"
              value={searchTerm}
              type="text"
              variant="outlined"
              onChange={e => setSearchTerm(e?.target.value || '')}
              InputProps={{
                sx: {
                  border: '1px solid #D4D4D4',
                },
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon htmlColor={colors.bodyText} fontSize="inherit" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <Box
            className="search-event_filter_click"
            sx={{
              maxHeight: '365px',
              overflow: 'auto',
            }}
          >
            {filteredEvents?.length ? (
              filteredEvents.map(event => (
                <EventItemRow
                  key={event.value}
                  event={event}
                  isSelected={includeEventIds.includes(event.value)}
                  isExcluded={false}
                  onSelect={(id: number) => {
                    trackActivity(EVENT_ACTIVITY_KEYS.SEARCH.EVENT_FILTER.CLICK, { event_name: event.label })
                    toggleIncludeEvent(id)
                  }}
                />
              ))
            ) : (
              <Typography variant="body1">{t(translationKeys.SEARCH_NO_RESULTS)}</Typography>
            )}
          </Box>
        </AccordionDetails>
      </BorderlessFilterAccordion>
    </OuterFilterBox>
  )
}
